import { c as _c } from "react/compiler-runtime";
import { useForm } from '@tanstack/react-form';
import { z } from 'zod';
import { Label } from '@/comps/primitives/label';
import { Input } from '@/comps/primitives/input';
import { Textarea } from '@/comps/primitives/textarea';
import { Button } from '@/comps/primitives/button';
import React, { useMemo } from 'react';
import { ChevronsUpDown, Info, Loader2Icon, Pencil, Wand } from 'lucide-react';
import { CategoryModel, GetInteractionAggregatesReq, GetInteractionAggregatesReqGroupBy } from '@/api/generated/model';
import { CreateCategoryDialogSection } from '@/domains/user-intelligence/groupby/components/customEntitties/CreateAggregateCategoryButton';
import { DialogClose } from '@/comps/primitives/dialog';
import { capitalize } from 'lodash';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
import { useFetchCustomAggregateCategories, useFetchInteractionAggregates } from '@/models/interaction-aggregates/useFetchInteractionAggregates';
import { GenericFilter } from '@/comps/FilterPicker/FilterPicker';
import { useProjectsContext } from '@/providers/projects-provider';
import { useGlobalFiltersContext } from '@/contexts/filters/filter-context';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/comps/primitives/command';
import { humanizeString } from '@/utils/string.utils';
import { useGenerateTopicDescription } from '@/api/generated/open-a-i/open-a-i';
import { WithLoadingContainer } from '@/comps/loading-spinner';
import { DialogConfirm } from '@/comps/dialog-confirm';
import { useAliasedKindName } from '@/contexts/alias';
import { translate } from '@/utils/translations';
const MIN_NAME_LENGTH = 1;
const MIN_DESCRIPTION_LENGTH = 50;
export type CustomAggregateCategorySubmitParams = {
  parent?: {
    name: string;
    kind: string;
  };
  name: string;
  description: string;
  reprocess: boolean;
  reprocessFrom: Date | undefined;
};
type CustomEntityDialogProps = {
  onSubmit: (params: CustomAggregateCategorySubmitParams) => void;
  entity?: CategoryModel;
  showReprocess?: boolean;
  kind?: string;
  mode: 'create' | 'edit';
  name?: string;
  selectedItems?: string[];
  cancelButton?: React.ReactNode;
};
type FormState = {
  parent?: {
    name: string;
    kind: string;
  };
  name: string;
  description: string;
  reprocess?: boolean;
  reprocessFrom?: Date;
};
export function CustomAggregateCategoryDialogForm(t0) {
  const $ = _c(74);
  const {
    onSubmit,
    entity,
    kind,
    mode,
    name,
    selectedItems,
    cancelButton
  } = t0;
  const aliasKindName = useAliasedKindName();
  let t1;
  if ($[0] !== aliasKindName) {
    const parentTopicLabel = aliasKindName("topic") ?? translate("filter", "topic");
    t1 = parentTopicLabel.toLowerCase();
    $[0] = aliasKindName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const parentTopicLabelLower = t1;
  const {
    projectId
  } = useProjectsContext();
  const {
    timeRange,
    filters,
    aggregateBy
  } = useGlobalFiltersContext();
  const [isLoading, setIsLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  let t2;
  if ($[2] !== entity?.value || $[3] !== name) {
    t2 = capitalize(name) || entity?.value || "";
    $[2] = entity?.value;
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = entity?.description || "";
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = {
      parent: undefined,
      name: t2,
      description: t3,
      reprocess: false,
      reprocessFrom: undefined
    };
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = t4 as FormState;
  let t6;
  if ($[8] !== kind) {
    t6 = z.object({
      parent: kind !== "action" ? z.object({
        name: z.string(),
        kind: z.string()
      }).optional() : z.object({
        name: z.string(),
        kind: z.string()
      }),
      name: z.string().min(MIN_NAME_LENGTH, "Name is required"),
      description: z.string().min(MIN_DESCRIPTION_LENGTH, `Description must be at least ${MIN_DESCRIPTION_LENGTH} characters`),
      reprocess: z.boolean().optional(),
      reprocessFrom: z.date().optional()
    });
    $[8] = kind;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = {
      onChange: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onSubmit) {
    t8 = t9 => {
      const {
        value: formValue
      } = t9;
      const {
        parent,
        name: name_0,
        description,
        reprocess,
        reprocessFrom
      } = formValue;
      onSubmit({
        parent,
        name: name_0,
        description,
        reprocess: !!reprocess,
        reprocessFrom
      });
    };
    $[12] = onSubmit;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t5 || $[15] !== t7 || $[16] !== t8) {
    t9 = {
      defaultValues: t5,
      validators: t7,
      onSubmit: t8
    };
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const form = useForm(t9);
  let t10;
  if ($[18] !== form) {
    t10 = {
      mutation: {
        onSuccess: t11 => {
          const {
            description: description_0
          } = t11;
          form.setFieldValue("description", description_0);
          form.validate("submit");
        }
      }
    };
    $[18] = form;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const {
    mutate: generateTopicDescription,
    isLoading: isGeneratingTopicDescription
  } = useGenerateTopicDescription(t10);
  const [debouncedSearch] = React.useState("");
  let t11;
  if ($[20] !== debouncedSearch) {
    t11 = debouncedSearch ? [{
      column: "topic",
      values: [],
      search: debouncedSearch,
      is_excluded: false
    } as GenericFilter] : [];
    $[20] = debouncedSearch;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== filters || $[23] !== t11) {
    t12 = [...filters, ...t11];
    $[22] = filters;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      kind: "topic"
    };
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== projectId || $[27] !== t12 || $[28] !== timeRange) {
    t14 = {
      filters: t12,
      project_id: projectId,
      time_range: timeRange,
      limit: 100000,
      group_by: t13 as GetInteractionAggregatesReqGroupBy
    };
    $[26] = projectId;
    $[27] = t12;
    $[28] = timeRange;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const aggregatePayload = t14;
  const t15 = !!projectId && kind === "action";
  let t16;
  if ($[30] !== aggregateBy || $[31] !== aggregatePayload || $[32] !== t15) {
    t16 = {
      payload: aggregatePayload,
      enabled: t15,
      aggregateBy
    };
    $[30] = aggregateBy;
    $[31] = aggregatePayload;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const {
    data: aggregatesData,
    isLoading: isLoadingAggregates
  } = useFetchInteractionAggregates(t16);
  let t17;
  if ($[34] !== projectId) {
    t17 = {
      payload: {
        project_id: projectId,
        aggregate_kind: "topic"
      }
    };
    $[34] = projectId;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const {
    data: customAggregateCategoriesData,
    isLoading: isLoadingCustomAggregateCategories
  } = useFetchCustomAggregateCategories(t17);
  let t18;
  bb0: {
    if (isLoadingCustomAggregateCategories || isLoadingAggregates) {
      let t19;
      if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = [];
        $[36] = t19;
      } else {
        t19 = $[36];
      }
      t18 = t19;
      break bb0;
    }
    let t19;
    if ($[37] !== aggregatesData?.aggregates || $[38] !== customAggregateCategoriesData) {
      const a = aggregatesData?.aggregates.map(_temp) ?? [];
      const b = customAggregateCategoriesData?.map(_temp2) ?? [];
      const mergedMap = new Map();
      [...a, ...b].forEach(name_1 => {
        const lower = name_1.toLowerCase();
        if (!mergedMap.has(lower)) {
          mergedMap.set(lower, name_1);
        }
      });
      t19 = Array.from(mergedMap.values()).sort(_temp3);
      $[37] = aggregatesData?.aggregates;
      $[38] = customAggregateCategoriesData;
      $[39] = t19;
    } else {
      t19 = $[39];
    }
    t18 = t19;
  }
  const combined = t18;
  let t19;
  if ($[40] !== form) {
    t19 = e_0 => {
      setIsLoading(true);
      e_0.preventDefault();
      form.handleSubmit();
    };
    $[40] = form;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== combined || $[43] !== form.Field || $[44] !== isLoadingAggregates || $[45] !== isLoadingCustomAggregateCategories || $[46] !== kind || $[47] !== open || $[48] !== parentTopicLabelLower) {
    t20 = kind === "action" && <form.Field name="parent">{field => {
        const fieldErrors = field.state.meta.errors;
        const isValid = fieldErrors.length === 0;
        const showError = !isValid;
        const errorMessage = fieldErrors[0]?.message;
        return <CreateCategoryDialogSection title={<Label htmlFor={field.name}>Parent</Label>} icon={<Pencil className="size-3" />}><Popover open={open} onOpenChange={setOpen}><PopoverTrigger asChild={true}><Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between">{field.state.value ? humanizeString(combined.find(c => c === field.state.value?.name)) : `Select ${parentTopicLabelLower}...`}<ChevronsUpDown className="opacity-50" /></Button></PopoverTrigger><PopoverContent onWheelCapture={_temp4} onTouchMoveCapture={_temp5} className="w-127.5 p-0"><WithLoadingContainer loading={isLoadingAggregates || isLoadingCustomAggregateCategories} hideChildren={true}><Command><CommandInput placeholder={`Search ${parentTopicLabelLower}...`} className="h-9" /><CommandList><CommandEmpty>{`No ${parentTopicLabelLower}s found.`}</CommandEmpty><CommandGroup>{combined.map(c_0 => <CommandItem key={c_0} value={c_0} onSelect={() => {
                        field.handleChange({
                          kind: "topic",
                          name: c_0
                        });
                        setOpen(false);
                      }}>{humanizeString(c_0)}</CommandItem>)}</CommandGroup></CommandList></Command></WithLoadingContainer></PopoverContent></Popover>{showError && <p className="mt-1 min-h-[1.5em] text-xs text-red-500">{errorMessage}</p>}</CreateCategoryDialogSection>;
      }}</form.Field>;
    $[42] = combined;
    $[43] = form.Field;
    $[44] = isLoadingAggregates;
    $[45] = isLoadingCustomAggregateCategories;
    $[46] = kind;
    $[47] = open;
    $[48] = parentTopicLabelLower;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== form.Field) {
    t21 = <form.Field name="name">{_temp6}</form.Field>;
    $[50] = form.Field;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  let t22;
  if ($[52] !== form.state.values.name || $[53] !== generateTopicDescription || $[54] !== isGeneratingTopicDescription || $[55] !== selectedItems) {
    t22 = field_1 => {
      const fieldErrors_0 = field_1.state.meta.errors;
      const isValid_0 = fieldErrors_0.length === 0;
      const showError_0 = field_1.state.value.length > 0 && field_1.state.meta.isTouched && !isValid_0;
      const errorMessage_0 = fieldErrors_0[0]?.message;
      return <><CreateCategoryDialogSection title={<Tooltip><TooltipTrigger><Label className="flex items-center gap-1" htmlFor={field_1.name}>Description <Info className="size-3 text-muted-foreground" /></Label></TooltipTrigger><TooltipContent className="flex flex-col gap-2"><p>Examples of descriptions:</p><i>"inquiries about mobile plans, data packages, roaming options, mobile number portability, and related support for mobile network services, mobile services offers and anything related to mobile services"</i><i>"all the user queries related to the management of the bank account such as password reset, account opening and closure, personal data changes etc."</i><i>"Users are asking about how to report worked hours and justify absences or schedule variations"</i></TooltipContent></Tooltip>} icon={<Pencil className="size-3" />}><Textarea className="min-h-25" value={field_1.state.value} disabled={isGeneratingTopicDescription} onChange={ev_0 => field_1.handleChange(ev_0.target.value)} required={true} aria-label="Category Description" placeholder="All the user queries related to the management of the bank account such as password reset, account opening and closure, personal data changes etc." minLength={MIN_DESCRIPTION_LENGTH} /><p className="mt-1 min-h-[1.5em] text-xs text-red-500">{showError_0 && errorMessage_0}</p><Button variant="outline" PrependIcon={isGeneratingTopicDescription ? Loader2Icon : Wand} iconClassName={isGeneratingTopicDescription ? "animate-spin" : ""} onClick={() => {
            generateTopicDescription({
              data: {
                name: form.state.values.name,
                selected_items: selectedItems
              }
            });
          }} disabled={isGeneratingTopicDescription || form.state.values.name.length < 1}>Generate with AI</Button></CreateCategoryDialogSection></>;
    };
    $[52] = form.state.values.name;
    $[53] = generateTopicDescription;
    $[54] = isGeneratingTopicDescription;
    $[55] = selectedItems;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== form.Field || $[58] !== t22) {
    t23 = <form.Field name="description">{t22}</form.Field>;
    $[57] = form.Field;
    $[58] = t22;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== cancelButton || $[61] !== form || $[62] !== isLoading || $[63] !== mode) {
    t24 = t25 => {
      const {
        canSubmit,
        isTouched
      } = t25;
      return <CreateCategoryDialogSection><footer className="flex items-center justify-end gap-2">{cancelButton ? cancelButton : <DialogClose><Button type="button" variant="outline">Cancel</Button></DialogClose>}<DialogConfirm description="The provided description will be used to process new data. A bad description may lead to incorrect categorization of your data. Do you wish to proceed?" onConfirm={() => {
            setIsLoading(true);
            form.handleSubmit();
          }} onCancel={() => {
            setOpen?.(false);
          }}><Button disabled={isLoading || !canSubmit || !isTouched} type="button">{isLoading && <Loader2Icon className="animate-spin" />}{mode === "create" ? "Create" : "Update"}</Button></DialogConfirm></footer></CreateCategoryDialogSection>;
    };
    $[60] = cancelButton;
    $[61] = form;
    $[62] = isLoading;
    $[63] = mode;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== form.Subscribe || $[66] !== t24) {
    t25 = <form.Subscribe selector={_temp7}>{t24}</form.Subscribe>;
    $[65] = form.Subscribe;
    $[66] = t24;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== t19 || $[69] !== t20 || $[70] !== t21 || $[71] !== t23 || $[72] !== t25) {
    t26 = <div><form onSubmit={t19}>{t20}{t21}{t23}{t25}</form></div>;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
    $[71] = t23;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  return t26;
}
function _temp7(state) {
  return {
    canSubmit: state.canSubmit,
    isTouched: state.isTouched
  };
}
function _temp6(field_0) {
  return <CreateCategoryDialogSection title={<Label htmlFor={field_0.name}>Name</Label>} icon={<Pencil className="size-3" />}><Input placeholder="Account management" value={field_0.state.value} autoFocus={true} onChange={ev => field_0.handleChange(ev.target.value)} required={true} aria-label="Category Name" /></CreateCategoryDialogSection>;
}
function _temp5(e_2) {
  return e_2.stopPropagation();
}
function _temp4(e_1) {
  return e_1.stopPropagation();
}
function _temp3(x, y) {
  return x.localeCompare(y);
}
function _temp2(e) {
  return e.value;
}
function _temp(agg) {
  return agg.groupName;
}